<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        class="event-edit-btn"
        icon 
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="green"
      title="Event"
      text="Edit Event"
      class="mb-0"
    >
      <form v-if="dialog">
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="localEvent.name"
                v-validate="'required'"
                :error-messages="errors.collect('name')"
                data-vv-name="name"                
                label="Name"
              />
            </v-col>

            <v-col cols="6">
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="startDateDisplay"
                    label="Start Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="localEvent.start_date"
                  @input="startDateMenu = false"
                />
              </v-menu>
            </v-col>
            
            <v-col cols="6">
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="endDateDisplay"
                    label="End Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="localEvent.end_date"
                  @input="endDateMenu = false"
                />
              </v-menu>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="localEvent.description"
                solo
                name="input-7-4"
                label="Description"
                placeholder="Event description"
              />
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="localEvent.original_folder_path"
                v-validate="'required'"
                data-vv-name="original_folder_path"
                data-vv-as="shared folder link"
                :error-messages="errors.collect('original_folder_path')"
                solo
                name="input-7-4"
                label="Shared Folder Link"
                placeholder="Shared Folder Link"
              />
            </v-col>

            <v-col cols="12">
              <helper-image-input  
                v-model="image" 
                :max-size-in-mb="2"
                data-vv-name="image"
                field-name="image"
                accept-type=".jpg,.jpeg,.png"            
              >          
                <div 
                  slot="activator"
                  class="mb-2"
                >   
                  <v-btn                    
                    :disabled="progressing"
                    class="mx-2"    
                  >
                    Add Image
                  </v-btn>
                  <span>
                    {{ image ? image.imageFile.name : 'No file chosen (.jpg/.png only)' }}
                  </span>
                </div>
              </helper-image-input>
              <span class="red--text text--lighten-1">{{ errors.first('image') }}</span>
            </v-col>
          </v-row>
        </v-container>
      </form>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="success"          
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    inject: ['$validator'],
    components: {
      HelperImageInput: () => import('@/components/helper/ImageInput'),
    },
    props: {
      event: {
        type: Object,
        default: null,
      },
    },
    data () {
      return {
        dialog: null,
        progressing: false,
        localEvent: null,
        image: null,
        startDateMenu: false,
        endDateMenu: false,
      }
    },
    computed: {
      startDateDisplay () {
        return this.localEvent.start_date ? this.$moment(this.localEvent.start_date, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      endDateDisplay () {
        return this.localEvent.end_date ? this.$moment(this.localEvent.end_date, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return
        this.localEvent = { ...this.event }
      },
    },
    methods: { 
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.setProgressing(true)
            this.$api.events().update(this.event.id, this.prepareFormData())
              .then(response => {
                if (response.status === 200) {
                  this.$emit('updated')
                  this.$snack({ message: response.data.status })

                  this.dialog = false
                }
              })
              .catch(() => {})
              .then(() => {
                this.setProgressing(false)
              })
          })
      },
      setProgressing (status) {
        this.progressing = status
      },
      prepareFormData () {
        if (!this.image) {
          return {
            'name': this.localEvent.name,
            'description': this.localEvent.description,
            'start_date': this.localEvent.start_date,
            'end_date': this.localEvent.end_date,
            'original_folder_path': this.localEvent.original_folder_path,
          }
        }

        this.image.formData.set('name', this.localEvent.name)
        this.image.formData.set('description', this.localEvent.description)
        this.image.formData.set('start_date', this.localEvent.start_date)
        this.image.formData.set('end_date', this.localEvent.end_date)
        this.image.formData.set('original_folder_path', this.localEvent.original_folder_path)

        return this.image.formData
      },
    },
  }
</script>

<style scoped>
.event-edit-btn{
  position: absolute;
  right: 10px;
  top: 10px;
}
</style>
